import type { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

// Draw order matters: the 2026 trophy sits on top of its neighbors.
const TROPHIES = [
    { year: "2021", className: styles.trophy2021 },
    { year: "2023", className: styles.trophy2023 },
    { year: "2022", className: styles.trophy2022 },
    { year: "2024", className: styles.trophy2024 },
    { year: "2026", className: styles.trophy2026 },
] as const;

const YEARS = [
    { year: "2021", className: styles.year2021 },
    { year: "2023", className: styles.year2023 },
    { year: "2026", className: styles.year2026 },
    { year: "2024", className: styles.year2024 },
    { year: "2022", className: styles.year2022 },
] as const;

const Category = (props: Immutable<{ name: string }>): React.JSX.Element => (
    <li className={styles.category}>
        <span className={styles.rank}>#1</span> <span>{props.name}</span>
    </li>
);

/**
 * JD Power 2026 online award banner.
 *
 * - ``overlay`` (#37284): the trophies stick out above the banner and sit on
 *   top of the block before it. Desktop shows everything in one row.
 * - ``blue`` (#37286): a mobile-only module on a blue background, with the
 *   headline above the trophies.
 */
export const JdPower2026Banner = (
    props: Immutable<{
        variant: "overlay" | "blue";
        learnMoreHref: string | null;
    }>,
): React.JSX.Element => {
    const staticURL = useStaticURL();
    const isOverlay = props.variant === "overlay";
    const headline = (
        <h3 className={styles.headline}>
            #1 in Customer Satisfaction with Mattresses Purchased Online{" "}
            <span className={styles.attribution}>
                — by JD Power<sup>+</sup>
            </span>
        </h3>
    );
    return (
        <div className={concatClassNames([styles.root, styles[props.variant]])}>
            {isOverlay ? null : headline}
            <div
                className={styles.trophies}
                role="img"
                aria-label="J.D. Power award trophies for 2021, 2022, 2023, 2024 and 2026"
            >
                {TROPHIES.map((t) => (
                    <img
                        key={t.year}
                        src={`${staticURL}img/hardcoded/jdpower-2026/trophy-${t.year}.png`}
                        alt=""
                        className={t.className}
                        loading="lazy"
                        decoding="async"
                    />
                ))}
                {YEARS.map((y) => (
                    <span key={y.year} className={y.className} aria-hidden>
                        {y.year}
                    </span>
                ))}
            </div>
            {isOverlay ? <div className={styles.divider} /> : null}
            <div className={styles.content}>
                {isOverlay ? headline : null}
                <div className={styles.categories}>
                    <ul className={styles.group}>
                        <Category name="Comfort" />
                        <Category name="Support" />
                        <Category name="Durability" />
                    </ul>
                    <ul className={styles.group}>
                        <Category name="Customer Service" />
                    </ul>
                    {props.learnMoreHref ? (
                        <a
                            className={styles.learnMore}
                            href={props.learnMoreHref}
                        >
                            Learn More
                        </a>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
